/**
 * apps/automations-portal/src/features/admin/LedgerOverviewPage.tsx
 *
 * Ledger landing page: one row per client, rolling up how many of their
 * automations are running, what happened last, and whether anything needs
 * attention. The API already sorts needs-attention clients first — this page
 * does not re-sort, it just makes that ordering read as intentional.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { adminApi } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import type { OverviewRow } from '@/lib/types';

export function LedgerOverviewPage() {
  const [rows, setRows] = useState<OverviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    adminApi
      .ledgerOverview()
      .then(setRows)
      .catch(() => setError('Could not load the ledger. Try refreshing the page.'));
  }, []);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Ledger</h1>
        <p className="text-sm text-muted-foreground">What every client&apos;s automations have been doing.</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!error && !rows && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {rows?.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No client activity yet.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <OverviewRowCard key={row.clientId} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewRowCard({ row }: { row: OverviewRow }) {
  const needsAttention = row.openApprovals > 0 || row.errors > 0;

  return (
    <Link
      to={`/admin/ledger/clients/${row.clientId}`}
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between',
        needsAttention && 'border-l-4 border-l-warning bg-warning/5',
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.openApprovals > 0 && <Badge variant="warning">{row.openApprovals} waiting</Badge>}
          {row.errors > 0 && <Badge variant="destructive">{row.errors} errors</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">
          {row.blocksEnabled} of {row.blocksTotal} automations running
        </p>
      </div>
      <div className="flex flex-col gap-0.5 sm:text-right">
        <span className="text-xs text-muted-foreground">{formatRelativeTime(row.lastEventAt)}</span>
        <span className="text-sm">{row.lastEventSummary ?? 'No activity yet'}</span>
      </div>
    </Link>
  );
}
